import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API, api } from "./AuthContext";

const VAR_MAP = { fond: "--background", cartes: "--card", texte: "--foreground", accent: "--accent", boutons: "--primary" };

function hexToHslTriplet(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function colorVars(obj) {
  return Object.entries(obj || {})
    .map(([k, v]) => { const t = hexToHslTriplet(v); return t && VAR_MAP[k] ? `${VAR_MAP[k]}: ${t};` : ""; })
    .filter(Boolean);
}

function applyBranding(b) {
  let tag = document.getElementById("branding-style");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "branding-style";
    document.head.appendChild(tag);
  }
  const lines = [];
  const fonts = new Set();
  const typo = b?.typo || {};
  const boutons = b?.boutons || {};
  const rootVars = colorVars(b?.couleurs?.sombre);
  if (boutons.arrondi !== undefined) rootVars.push(`--radius: ${boutons.arrondi}px;`);
  if (rootVars.length) lines.push(`:root { ${rootVars.join(" ")} }`);
  const lightVars = colorVars(b?.couleurs?.clair);
  if (lightVars.length) lines.push(`.light { ${lightVars.join(" ")} }`);
  if (typo.police_texte) { fonts.add(typo.police_texte); lines.push(`body { font-family: '${typo.police_texte}', sans-serif !important; }`); }
  if (typo.police_titres) { fonts.add(typo.police_titres); lines.push(`h1, h2, h3, h4 { font-family: '${typo.police_titres}', sans-serif !important; }`); }
  if (typo.taille_base) lines.push(`body { font-size: ${typo.taille_base}px; }`);
  const btnRules = [];
  if (boutons.majuscules) btnRules.push("text-transform: uppercase");
  if (boutons.gras) btnRules.push("font-weight: 700");
  if (btnRules.length) lines.push(`button { ${btnRules.join("; ")}; }`);
  tag.textContent = lines.join("\n");
  if (fonts.size) {
    let link = document.getElementById("branding-fonts");
    if (!link) {
      link = document.createElement("link");
      link.id = "branding-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = "https://fonts.googleapis.com/css2?" +
      [...fonts].map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700;800`).join("&") + "&display=swap";
  }
}

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState({});
  const [images, setImages] = useState({});
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => {
    api.get("/branding")
      .then((r) => { setBranding(r.data.branding || {}); setImages(r.data.images || {}); setVersion((v) => v + 1); })
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { applyBranding(branding); }, [branding]);
  useEffect(() => {
    const s = branding?.site || {};
    const titre = [s.titre_l1, s.titre_l2].filter(Boolean).join(" ");
    if (titre) document.title = titre;
  }, [branding]);

  const label = useCallback((key, fallback) => branding?.menu?.[key] || fallback, [branding]);
  const imageUrl = useCallback((kind) => (images?.[kind] ? `${API}/branding/image/${kind}?v=${version}` : null), [images, version]);

  return <BrandingContext.Provider value={{ branding, images, refresh, label, imageUrl }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
